import React, { useMemo, useState } from 'react';
import type { MenuItem } from '../../types';
import { useSoftKeyboardField } from '../../hooks/useSoftKeyboardField';
import { formatLiveFishDetail, liveFishLineTotal, parseLiveFishAmount } from '../../lib/liveFishPrice';

export interface LiveFishSale {
  fish: string;
  weightKg: number;
  pricePerKg: number;
}

interface LiveFishModalProps {
  item: MenuItem;
  onConfirm: (item: MenuItem, sale: LiveFishSale) => void;
  onCancel: () => void;
}

const LiveFishModal: React.FC<LiveFishModalProps> = ({ item, onConfirm, onCancel }) => {
  const fishNames = (item.weightFishOptions ?? []).map((name) => name.trim()).filter(Boolean);
  const [selectedFish, setSelectedFish] = useState('');
  const [weightInput, setWeightInput] = useState('');
  const [priceInput, setPriceInput] = useState('');

  const weightKeyboard = useSoftKeyboardField({
    id: 'live-fish-weight',
    type: 'numeric',
    label: 'Weight (kg)',
    value: weightInput,
    onChange: setWeightInput,
    manualValidator: (v) => v === '' || /^\d*\.?\d{0,3}$/.test(v),
  });
  const priceKeyboard = useSoftKeyboardField({
    id: 'live-fish-price-per-kg',
    type: 'numeric',
    label: 'Price per kg',
    value: priceInput,
    onChange: setPriceInput,
    manualValidator: (v) => v === '' || /^\d*\.?\d{0,2}$/.test(v),
  });

  const weightKg = parseLiveFishAmount(weightInput, 3);
  const pricePerKg = parseLiveFishAmount(priceInput, 2);
  const total = weightKg != null && pricePerKg != null ? liveFishLineTotal(weightKg, pricePerKg) : null;
  const detail = useMemo(
    () => (selectedFish && weightKg != null && pricePerKg != null
      ? formatLiveFishDetail({ selectedFish, weightKg, pricePerKg })
      : null),
    [selectedFish, weightKg, pricePerKg],
  );
  const canConfirm = fishNames.length > 0 && !!selectedFish && total != null;

  const handleConfirm = () => {
    if (!canConfirm || weightKg == null || pricePerKg == null || total == null) return;
    onConfirm(item, { fish: selectedFish, weightKg, pricePerKg });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4 overflow-y-auto" data-testid="live-fish-modal">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto my-auto">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Live fish</h2>
        <p className="text-gray-600 mb-4">Choose the fish, then enter the weight and price per kg for <strong>{item.name}</strong>.</p>

        {fishNames.length === 0 ? (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3 mb-6">
            Add fish names on this menu item in Menu Management before selling it.
          </p>
        ) : (
          <div className="space-y-2 mb-4">
            {fishNames.map((name) => (
              <button
                key={name}
                type="button"
                data-testid="live-fish-choice"
                onClick={() => setSelectedFish(name)}
                className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${
                  selectedFish === name
                    ? 'border-pink-500 bg-pink-50 text-pink-800 font-semibold'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label htmlFor="live-fish-weight" className="block text-sm font-medium text-gray-700 mb-1">Weight (kg)</label>
            <input
              id="live-fish-weight"
              type="text"
              inputMode="decimal"
              value={weightInput}
              data-soft-keyboard-handled
              {...weightKeyboard}
              placeholder="0.000"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
            />
          </div>
          <div>
            <label htmlFor="live-fish-price-per-kg" className="block text-sm font-medium text-gray-700 mb-1">Price per kg</label>
            <input
              id="live-fish-price-per-kg"
              type="text"
              inputMode="decimal"
              value={priceInput}
              data-soft-keyboard-handled
              {...priceKeyboard}
              placeholder="0.00"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
            />
          </div>
        </div>

        <div className="mb-6 rounded-md bg-gray-50 border border-gray-200 px-4 py-3">
          <p className="text-sm text-gray-600">{detail ?? 'Total'}</p>
          <p className="text-2xl font-bold text-gray-900" data-testid="live-fish-total">
            {total != null ? `$${total.toFixed(2)}` : '$0.00'}
          </p>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={onCancel}
            data-testid="live-fish-cancel"
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            data-testid="live-fish-add"
            className={`px-4 py-2 rounded-md font-semibold ${
              canConfirm
                ? 'bg-pink-500 text-white hover:bg-pink-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Add to Order
          </button>
        </div>
      </div>
    </div>
  );
};

export default LiveFishModal;
